import React, { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/native';
import { TouchableOpacity } from 'react-native';
import { Calendar as RNCalendar, DateData } from 'react-native-calendars';
import { Direction, MarkedDates } from 'react-native-calendars/src/types';
import XDate from 'xdate';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { Feather } from '@expo/vector-icons';
import { useMutation, useQuery } from '@apollo/client';
import { useShallow } from 'zustand/react/shallow';
import { ActivityIndicator } from 'react-native-paper';

import { GET_STYLIST_SCHEDULING_DATA } from '@/shared/queries/stylistSchema';
import { SET_DATE_AVAILABILITY } from '@/shared/mutations';
import useAvailabilityStore from '@/stores/useAvailabilityStore';
import useStore from '@/stores/useStore';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { LoraText } from '@/shared/components/StyledText';
import { FONT_STYLES } from '@/shared/styles/constants';
import {
  DateAvailabilitySlotInput,
  MutationResult,
  QueryResult,
  StylistAvailabilitySlot,
} from '@/shared/types/generated';
import { StepComponentProps } from '@/shared/types/availability';
import {
  accent,
  alphaBlack50,
  calendarBackground,
  green,
  lightGreen,
  mint,
  mintGreenAlt,
  white,
} from '@/shared/styles/colors';
import {
  CALENDAR_DAY_FORMAT,
  CALENDAR_MONTH_FORMAT_DISPLAY,
} from '@/shared/constants/calendar';
import { AVAILABILITY_HORIZON_DAYS } from '@/shared/constants/availability';
import SectionLayout from '../SectionLayout';
import { mergeAdjacentSlots } from '../utils';

// ─── Constants ───────────────────────────────────────────────────────────────

const SLOT_START_HOUR = 8;
const SLOT_END_HOUR = 22;
const SLOTS_PER_HOUR = 2;
const TOTAL_SLOTS = (SLOT_END_HOUR - SLOT_START_HOUR) * SLOTS_PER_HOUR;

interface TimeSlot {
  startTime: string; // "HH:mm"
  endTime: string; // "HH:mm"
  label: string; // "9:00 AM"
}

const TIME_SLOTS: TimeSlot[] = Array.from(
  { length: TOTAL_SLOTS },
  (_unused, index) => {
    const totalMinutes = SLOT_START_HOUR * 60 + index * 30;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

    const endMinutes = totalMinutes + 30;
    const endHour = Math.floor(endMinutes / 60);
    const endMinute = endMinutes % 60;
    const endTime = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

    const hour12 = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    const label = `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;

    return { startTime, endTime, label };
  },
);

// ─── Component ───────────────────────────────────────────────────────────────

const DateSchedule = ({ onSaveAvailability }: StepComponentProps) => {
  const { stylistId } = useAvailabilityStore(
    useShallow((state) => ({ stylistId: state.stylistId })),
  );
  const { setSnackbarState } = useStore();
  const { isDesktop } = useBreakpoint();

  const today = format(startOfDay(new Date()), CALENDAR_DAY_FORMAT);
  const maxDate = format(
    addDays(startOfDay(new Date()), AVAILABILITY_HORIZON_DAYS),
    CALENDAR_DAY_FORMAT,
  );

  // date → sorted array of startTime strings
  const [saved, setSaved] = useState<Record<string, string[]>>({});
  const [pending, setPending] = useState<Record<string, string[]>>({});
  const [selectedDate, setSelectedDate] = useState<string>(today);
  const [isSaving, setIsSaving] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [schedulingError, setSchedulingError] = useState<string | null>(null);

  const { loading, refetch } = useQuery<QueryResult<'schedulingData'>>(
    GET_STYLIST_SCHEDULING_DATA,
    {
      fetchPolicy: 'cache-first',
      onCompleted: ({ schedulingData }) => {
        setSchedulingError(null);
        const slots: StylistAvailabilitySlot[] =
          schedulingData?.availabilitySlots?.filter(
            (slot): slot is StylistAvailabilitySlot => !!slot,
          ) ?? [];
        const grouped: Record<string, string[]> = {};
        for (const slot of slots) {
          if (!grouped[slot.date]) {
            grouped[slot.date] = [];
          }
          const matching = TIME_SLOTS.filter(
            (ts) =>
              ts.startTime >= slot.startTime && ts.startTime < slot.endTime,
          );
          for (const timeSlot of matching) {
            if (!grouped[slot.date].includes(timeSlot.startTime)) {
              grouped[slot.date].push(timeSlot.startTime);
            }
          }
        }
        setSaved(grouped);
        setPending(
          Object.fromEntries(
            Object.entries(grouped).map(([date, times]) => [date, [...times]]),
          ),
        );
      },
      onError: (err) => {
        setSchedulingError(err.message);
        setSnackbarState({
          message: err.message,
          severity: 'error',
          open: true,
        });
      },
    },
  );

  const [setDateAvailability] = useMutation<
    MutationResult<'setDateAvailability'>
  >(SET_DATE_AVAILABILITY);

  const pendingForDate = pending[selectedDate] ?? [];

  const hasUnsavedChanges = useMemo(() => {
    const normalizedSaved = Object.fromEntries(
      Object.entries(saved).filter(([, slots]) => (slots ?? []).length > 0),
    );
    const normalizedPending = Object.fromEntries(
      Object.entries(pending).filter(([, slots]) => (slots ?? []).length > 0),
    );
    const savedKeys = Object.keys(normalizedSaved).sort();
    const pendingKeys = Object.keys(normalizedPending).sort();
    if (savedKeys.length !== pendingKeys.length) return true;
    for (let i = 0; i < savedKeys.length; i++) {
      const key = savedKeys[i];
      if (key !== pendingKeys[i]) return true;
      const a = [...(normalizedSaved[key] ?? [])].sort();
      const b = [...(normalizedPending[key] ?? [])].sort();
      if (a.length !== b.length) return true;
      for (let j = 0; j < a.length; j++) {
        if (a[j] !== b[j]) return true;
      }
    }
    return false;
  }, [saved, pending]);

  const toggleSlot = useCallback(
    (startTime: string) => {
      setPending((prev) => {
        const current = prev[selectedDate] ?? [];
        const next = current.includes(startTime)
          ? current.filter((time) => time !== startTime)
          : [...current, startTime];
        return { ...prev, [selectedDate]: next };
      });
    },
    [selectedDate],
  );

  const handleSaveAllAndAdvance = useCallback(async () => {
    if (!stylistId) {
      return;
    }
    setIsSaving(true);
    setIsAdvancing(true);
    try {
      const allSlots: DateAvailabilitySlotInput[] = [];
      // Dates where every time was unchecked — the server must delete their
      // previously saved slots, otherwise clients can still book them.
      const clearedDates: string[] = [];
      for (const [date, startTimes] of Object.entries(pending)) {
        // Only send dates the calendar allows — avoids re-submitting stale
        // out-of-horizon slots that would fail backend validation.
        if (date < today || date > maxDate) {
          continue;
        }
        if (startTimes.length === 0) {
          const hadSavedSlots = (saved[date] ?? []).length > 0;
          if (hadSavedSlots) {
            clearedDates.push(date);
          }
          continue;
        }
        const rawSlots = startTimes
          .map((startTime) => {
            const timeSlot = TIME_SLOTS.find(
              (candidate) => candidate.startTime === startTime,
            );
            if (!timeSlot) {
              return null;
            }
            return { date, startTime, endTime: timeSlot.endTime };
          })
          .filter((slot): slot is DateAvailabilitySlotInput => slot !== null);
        allSlots.push(...mergeAdjacentSlots(rawSlots));
      }

      if (allSlots.length === 0 && clearedDates.length === 0) {
        return;
      }

      await setDateAvailability({
        variables: { input: { stylistId, slots: allSlots, clearedDates } },
        refetchQueries: [{ query: GET_STYLIST_SCHEDULING_DATA }],
      });

      setSaved(
        Object.fromEntries(
          Object.entries(pending).map(([date, times]) => [date, [...times]]),
        ),
      );
      await onSaveAvailability();
    } catch {
      setSnackbarState({
        message:
          'There was an issue saving your availability. Please try again.',
        severity: 'error',
        open: true,
      });
    } finally {
      setIsSaving(false);
      setIsAdvancing(false);
    }
  }, [
    stylistId,
    pending,
    saved,
    today,
    maxDate,
    setDateAvailability,
    setSnackbarState,
    onSaveAvailability,
  ]);

  const markedDates: MarkedDates = useMemo(() => {
    const marks: MarkedDates = {};
    const hasSlots = (date: string) =>
      (pending[date] ?? []).length > 0 || (saved[date] ?? []).length > 0;
    for (const date of Object.keys(pending)) {
      if (date >= today && date <= maxDate && hasSlots(date)) {
        const isSelectedDay = date === selectedDate;
        if (isSelectedDay) {
          marks[date] = {
            selected: true,
            selectedColor: accent,
            selectedTextColor: green,
          };
        } else {
          marks[date] = {
            customStyles: {
              container: {
                backgroundColor: green,
                borderRadius: 16,
              },
              text: {
                color: white,
              },
            },
          };
        }
      }
    }
    for (const [date, times] of Object.entries(saved)) {
      if (Object.prototype.hasOwnProperty.call(pending, date)) {
        continue;
      }
      if (
        times.length > 0 &&
        date >= today &&
        date <= maxDate &&
        !marks[date]
      ) {
        const isSelectedDay = date === selectedDate;
        if (isSelectedDay) {
          marks[date] = {
            selected: true,
            selectedColor: accent,
            selectedTextColor: green,
          };
        } else {
          marks[date] = {
            customStyles: {
              container: {
                backgroundColor: green,
                borderRadius: 16,
              },
              text: {
                color: white,
              },
            },
          };
        }
      }
    }
    marks[selectedDate] = {
      ...(marks[selectedDate] ?? {}),
      selected: true,
      selectedColor: accent,
      selectedTextColor: green,
    };
    return marks;
  }, [saved, pending, selectedDate, today, maxDate]);

  const onDayPress = useCallback((day: DateData) => {
    setSelectedDate(day.dateString);
  }, []);

  function renderArrow(direction: Direction) {
    return (
      <ArrowCircle>
        <Feather
          name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
          size={24}
          color={lightGreen}
        />
      </ArrowCircle>
    );
  }

  function renderHeader(date?: XDate) {
    if (!date) {
      return null;
    }
    return (
      <MonthTitle>
        {date.toString(CALENDAR_MONTH_FORMAT_DISPLAY).toUpperCase()}
      </MonthTitle>
    );
  }

  const formattedSelectedDate = format(parseISO(selectedDate), 'EEEE, MMMM d');

  const calendarSection = (
    <CalendarWrapper isDesktop={isDesktop}>
      <RNCalendar
        markingType="custom"
        minDate={today}
        maxDate={maxDate}
        onDayPress={onDayPress}
        markedDates={markedDates}
        disableAllTouchEventsForDisabledDays
        renderArrow={renderArrow}
        renderHeader={renderHeader}
        theme={{
          calendarBackground,
          selectedDayBackgroundColor: accent,
          selectedDayTextColor: green,
          dayTextColor: green,
          textDisabledColor: mintGreenAlt,
          textDayFontFamily: FONT_STYLES.helvetica.fontFamily,
          textDayFontSize: 15,
          textDayFontWeight: 700,
          textSectionTitleColor: alphaBlack50,
          textDayHeaderFontSize: 12,
          textDayHeaderFontFamily: FONT_STYLES.helvetica.fontFamily,
          todayBackgroundColor: 'transparent',
          todayTextColor: green,
          ...({
            'stylesheet.day.basic': {
              today: {
                backgroundColor: 'transparent',
                borderWidth: 1,
                borderColor: green,
                borderRadius: 16,
              },
            },
            'stylesheet.calendar.main': {
              container: {
                paddingLeft: 0,
                paddingRight: 0,
                backgroundColor: calendarBackground,
              },
            },
            'stylesheet.calendar.header': {
              header: {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: 0,
                paddingRight: 0,
                flexWrap: 'nowrap',
              },
              headerContainer: {
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                paddingHorizontal: 8,
              },
              arrow: {
                padding: 0,
                alignSelf: 'center',
                flexShrink: 0,
              },
              week: {
                marginTop: 14,
                flexDirection: 'row',
                justifyContent: 'space-around',
              },
            },
          } as Parameters<typeof RNCalendar>[0]['theme']),
        }}
      />
    </CalendarWrapper>
  );

  const slotsSection = (
    <SlotsWrapper isDesktop={isDesktop}>
      <SlotsInstruction>Select date/times you are available:</SlotsInstruction>
      <SelectedDateContainer>
        <SelectedDateLabel>{formattedSelectedDate}</SelectedDateLabel>
      </SelectedDateContainer>
      <SlotsGrid>
        {TIME_SLOTS.map(({ startTime, label }) => {
          const isSelected = pendingForDate.includes(startTime);
          return (
            <SlotButton
              key={startTime}
              isDesktop={isDesktop}
              isSelected={isSelected}
              onPress={() => toggleSlot(startTime)}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: isSelected }}
              accessibilityLabel={`${label}, ${isSelected ? 'selected' : 'not selected'}`}
            >
              {isSelected && (
                <CheckmarkCircle isDesktop={isDesktop}>
                  <Feather
                    name="check"
                    size={isDesktop ? 6 : 10}
                    color={green}
                  />
                </CheckmarkCircle>
              )}
              <SlotLabel isSelected={isSelected}>{label}</SlotLabel>
            </SlotButton>
          );
        })}
      </SlotsGrid>
    </SlotsWrapper>
  );

  const scheduleHeading = isDesktop
    ? 'CUSTOMIZE YOUR HOURS'
    : 'CUSTOMIZE\nYOUR HOURS';
  const scheduleDescription = "Tell clients when you're free";

  if (loading) {
    return (
      <LoadingContainer>
        <ActivityIndicator size="large" color={green} />
      </LoadingContainer>
    );
  }

  if (schedulingError) {
    return (
      <SectionLayout
        heading={scheduleHeading}
        description={scheduleDescription}
        buttonText="TRY AGAIN"
        onButtonPress={() => refetch()}
        isButtonDisabled={false}
        isButtonLoading={false}
        buttonAfterDescription
        titleMarginBottom={8}
        descriptionMarginBottom={isDesktop ? 12 : 20}
        descriptionStyle={isDesktop ? undefined : { lineHeight: 20 }}
        containerStyle={{
          paddingHorizontal: isDesktop ? 28 : 0,
          marginTop: 24,
        }}
        contentStyle={{
          maxWidth: isDesktop ? 760 : undefined,
          marginBottom: isDesktop ? 12 : 24,
        }}
      >
        <ErrorMessage>
          We couldn&apos;t load your schedule. Please try again.
        </ErrorMessage>
      </SectionLayout>
    );
  }

  return (
    <SectionLayout
      heading={scheduleHeading}
      description={scheduleDescription}
      buttonText="SAVE AVAILABILITY"
      onButtonPress={handleSaveAllAndAdvance}
      isButtonDisabled={!hasUnsavedChanges || isSaving || isAdvancing}
      isButtonLoading={isSaving || isAdvancing}
      buttonAfterDescription
      titleMarginBottom={8}
      descriptionMarginBottom={isDesktop ? 12 : 20}
      descriptionStyle={isDesktop ? undefined : { lineHeight: 20 }}
      containerStyle={{
        paddingHorizontal: isDesktop ? 28 : 0,
        marginTop: isDesktop ? 24 : 8,
      }}
      contentStyle={{
        maxWidth: isDesktop ? 760 : '100%',
        marginBottom: isDesktop ? 12 : 8,
      }}
    >
      {isDesktop ? (
        <DesktopLayout>
          {calendarSection}
          {slotsSection}
        </DesktopLayout>
      ) : (
        <MobileScheduleLayout>
          {calendarSection}
          {slotsSection}
        </MobileScheduleLayout>
      )}
    </SectionLayout>
  );
};

export default DateSchedule;

// ─── Styles ───────────────────────────────────────────────────────────────────

const DesktopLayout = styled.View({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 32,
  width: '100%',
});

const MobileScheduleLayout = styled.View({
  width: '100%',
  alignSelf: 'stretch',
});

const CalendarWrapper = styled.View<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    backgroundColor: calendarBackground,
    borderRadius: 8,
    padding: 20,
    ...(isDesktop
      ? { flex: 1, minWidth: 360, maxWidth: 460 }
      : {
          width: '100%',
          minWidth: 280,
          alignSelf: 'stretch',
        }),
  }),
);

const SlotsWrapper = styled.View<{ isDesktop?: boolean }>(({ isDesktop }) => ({
  width: '100%',
  marginTop: 0,
  paddingHorizontal: isDesktop ? 12 : 20,
  ...(!isDesktop && { paddingTop: 24, marginBottom: 24 }),
  ...(isDesktop && { flex: 1, minWidth: 0 }),
}));

const MonthTitle = styled(LoraText)({
  fontSize: 20,
  fontWeight: 500,
  color: green,
  letterSpacing: 0.18,
});

const ArrowCircle = styled.View({
  backgroundColor: mint,
  borderRadius: '50%',
  width: 36,
  height: 36,
  alignItems: 'center',
  justifyContent: 'center',
});

const SelectedDateContainer = styled.View({
  backgroundColor: mintGreenAlt,
  borderRadius: 8,
  paddingVertical: 12,
  paddingHorizontal: 16,
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
  width: '100%',
});

const SelectedDateLabel = styled.Text({
  ...FONT_STYLES.helvetica,
  fontSize: 14,
  fontWeight: '700',
  color: green,
  textAlign: 'center',
});

const SlotsInstruction = styled.Text({
  ...FONT_STYLES.helvetica,
  fontSize: 14,
  fontWeight: '400',
  color: alphaBlack50,
  marginBottom: 16,
});

const SlotsGrid = styled.View({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 8,
  width: '100%',
});

const SlotButton = styled(TouchableOpacity)<{
  isSelected: boolean;
  isDesktop?: boolean;
}>(({ isSelected, isDesktop }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: isDesktop ? '31%' : '48%',
  paddingVertical: isDesktop ? 14 : 20,
  paddingHorizontal: 8,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: green,
  backgroundColor: isSelected ? green : white,
  position: 'relative',
}));

const CheckmarkCircle = styled.View<{ isDesktop?: boolean }>(
  ({ isDesktop }) => ({
    position: 'absolute',
    top: isDesktop ? 6 : 10,
    left: isDesktop ? 6 : 10,
    width: isDesktop ? 10 : 18,
    height: isDesktop ? 10 : 18,
    borderRadius: isDesktop ? 5 : 9,
    backgroundColor: white,
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const SlotLabel = styled.Text<{ isSelected: boolean }>(({ isSelected }) => ({
  ...FONT_STYLES.lora,
  fontSize: 14,
  fontWeight: '400',
  color: isSelected ? white : green,
  textAlign: 'center',
}));

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const ErrorMessage = styled(LoraText)({
  color: alphaBlack50,
  textAlign: 'center',
});
